import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Boxes, Loader2, AlertTriangle, Plus, Link2, Link2Off, RefreshCw,
  Filter as FilterIcon, Activity, ShieldAlert, GitBranch,
  CheckCircle2, XCircle, ShieldCheck, ChevronDown, ChevronRight, FilePlus2,
} from 'lucide-react';
import { Button } from '../Button';
import { Slideout } from '../Slideout';
import { IntentLayersExplainer } from './IntentLayersExplainer';
import { EditIntentTemplateVersionSlideout } from './EditIntentTemplateVersionSlideout';
import { useAuth } from '../../../contexts/AuthContext';
import {
  listAgentIntentBindings,
  listAgentIntentBindingsEnriched,
  getAgentIntentBindingCoverage,
  getAgentSkills,
  bindAgentToIntentTemplateVersion,
  unbindAgentIntentBinding,
  narrowAgentIntentBinding,
  rebindAgentIntentBinding,
  detectAgentIntentBindingDrift,
  listIntentTemplates,
  listIntentTemplateVersions,
  createIntentTemplateVersion,
  // Phase 2C runtime-source helpers DELETED in V219 (Phase 5).
} from '../../../services/api';

/**
 * Intent Profiles Catalog v1.0 — Phase 1E.3
 *
 * Per-agent intent-bindings panel. Mounted on AgentDetailPageV2
 * ALONGSIDE the existing legacy inline IntentProfilesEditor. The two
 * coexist intentionally:
 *
 *   - Inline editor (legacy): runtime source-of-truth until Phase 1D's
 *     {@code agentlets.intent.runtime-read-from-catalog} flag flips.
 *   - This panel (catalog bindings): authoritative future-state surface;
 *     drives runtime when the flag is flipped (and exclusively after
 *     Phase 5 removes the inline column).
 *
 * <h3>Permission model</h3>
 * <p>Read: {@code agent.read} (which the caller already has — they're on
 * the agent detail page). Write actions (Bind / Unbind / Narrow / Rebind):
 * {@code agent.write} OR {@code compliance.admin} OR {@code policy.admin}.
 * Drift detection is open to anyone with read.</p>
 *
 * <h3>Phase-independent bind/unbind (Prove7-journey redesign)</h3>
 * <p>Intent is a first-class, independently-set step: bind + unbind are
 * allowed in ANY phase, including Act, with NO block. The Promote and
 * Execution-Ready gates CHECK intent_bound but never block setting it.
 * narrow() remains the strictly-tightening option. On bind, the approved
 * version's Rego is materialized into an agent-scoped PolicyRule the gateway
 * enforces + seals (INTENT).</p>
 */
export function IntentBindingsPanel({ agentId, agentPhase }) {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission('agent.write')
    || hasPermission('compliance.admin')
    || hasPermission('policy.admin');

  const [bindings, setBindings] = useState([]);
  const [state, setState] = useState('loading'); // loading | ready | error
  const [errorMsg, setErrorMsg] = useState(null);
  const [actionPendingId, setActionPendingId] = useState(null);
  const [actionError, setActionError] = useState(null); // {id, message}
  const [bindOpen, setBindOpen] = useState(false);
  // "Authorize N in a new version" flow — a new draft of THIS intent, opened inline for authoring.
  const [editorVersion, setEditorVersion] = useState(null);       // draft version to edit (null = closed)
  const [editorTemplateName, setEditorTemplateName] = useState('');
  const [authoringBusyId, setAuthoringBusyId] = useState(null);   // binding id currently starting a draft
  const [authoringNotice, setAuthoringNotice] = useState(null);   // next-step guidance after save
  // Skill id → display name, so the Narrow control can show skill names instead of raw ids.
  const [skillNameById, setSkillNameById] = useState({});
  // bindingId → coverage summary, computed eagerly so the header can show how many tools still
  // need authorizing and each binding shows its count without being expanded.
  const [coverageById, setCoverageById] = useState({});

  const load = useCallback(async () => {
    if (!agentId) return;
    setState('loading');
    setErrorMsg(null);
    setActionError(null);
    // Prefer the enriched list (carries the source intent's name + version). Fall back to the
    // plain list if the enriched route is unavailable, so the panel still renders.
    let bindingsRes = await listAgentIntentBindingsEnriched(agentId);
    if (bindingsRes?.ok === false) {
      bindingsRes = await listAgentIntentBindings(agentId);
    }
    if (bindingsRes?.ok === false) {
      setState('error');
      setErrorMsg(bindingsRes.error || 'Failed to load intent bindings');
      return;
    }
    const raw = bindingsRes?.data ?? bindingsRes;
    const list = Array.isArray(raw) ? raw : [];
    setBindings(list);
    setState('ready');
    // Eagerly compute authorization coverage (bounded to this agent's bindings) so the header can
    // show "N tools need authorizing" and each binding shows its count without expanding.
    if (list.length > 0) {
      Promise.all(list.map(async (b) => {
        const cRes = await getAgentIntentBindingCoverage(agentId, b.id);
        return [b.id, cRes?.ok === false ? null : (cRes?.data ?? cRes)];
      })).then((entries) => {
        const map = {};
        for (const [id, cov] of entries) map[id] = cov;
        setCoverageById(map);
      }).catch(() => { /* coverage is best-effort; the panel still renders without it */ });
    } else {
      setCoverageById({});
    }
  }, [agentId]);

  // Total connector tools across all bindings that this agent's intents do not yet authorize.
  const needsAuthTotal = useMemo(
    () => Object.values(coverageById).reduce((n, c) => n + (c?.unauthorizedCount || 0), 0),
    [coverageById],
  );

  useEffect(() => { load(); }, [load]);

  // Load the agent's skills once so Narrow can present skills by name.
  useEffect(() => {
    if (!agentId) return;
    let cancelled = false;
    (async () => {
      const res = await getAgentSkills(agentId);
      if (cancelled || res?.ok === false) return;
      const arr = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
      const map = {};
      for (const s of arr) if (s && s.id) map[s.id] = s.name || s.displayName || s.id;
      setSkillNameById(map);
    })();
    return () => { cancelled = true; };
  }, [agentId]);

  const runAction = async (bindingId, fn) => {
    setActionPendingId(bindingId);
    setActionError(null);
    const res = await fn();
    setActionPendingId(null);
    if (res?.ok === false) {
      setActionError({ id: bindingId, message: res.error || 'Action failed' });
      return false;
    }
    await load();
    return true;
  };

  // Start authoring a NEW version of the intent bound to this agent, pre-seeded to authorize the
  // tools it does not yet allow — opened inline here (no navigation away from the agent).
  const onAuthorizeNewVersion = async (binding, coverage) => {
    const templateId = binding?.templateId;
    const tokens = coverage?.unauthorizedTokens || [];
    if (!templateId) {
      setActionError({ id: binding?.id, message: 'This binding is missing its source intent, so a new version can’t be started here.' });
      return;
    }
    setAuthoringBusyId(binding.id);
    setActionError(null);
    setAuthoringNotice(null);
    // Base the new version on the Rego of the version currently bound to this agent.
    let baseRego = '';
    const versRes = await listIntentTemplateVersions(templateId);
    const versList = versRes?.data ?? versRes;
    if (Array.isArray(versList)) {
      const cur = versList.find((v) => v.id === binding.templateVersionId);
      baseRego = cur?.intentRego || '';
    }
    const createRes = await createIntentTemplateVersion(templateId);
    setAuthoringBusyId(null);
    if (createRes?.ok === false) {
      setActionError({
        id: binding.id,
        message: createRes.error
          || 'Couldn’t start a new version. A draft may already exist for this intent — finish or discard it first.',
      });
      return;
    }
    const draft = createRes?.data ?? createRes;
    setEditorVersion({ ...draft, intentRego: appendAuthorizeBlock(baseRego || draft.intentRego || '', tokens) });
    setEditorTemplateName(binding.templateDisplayName || binding.templateName || 'this intent');
  };

  // Move this agent from its current bound version to the intent's latest APPROVED version. A binding
  // pins one version deliberately, so adopting a newer version is explicit: bind the new one first
  // (fail-safe — the agent always keeps a binding), then detach the old.
  const onUpdateToVersion = async (binding) => {
    const targetVersionId = binding?.latestApprovedVersionId;
    if (!targetVersionId) return;
    setActionPendingId(binding.id);
    setActionError(null);
    setAuthoringNotice(null);
    const bindRes = await bindAgentToIntentTemplateVersion(agentId, targetVersionId);
    if (bindRes?.ok === false) {
      setActionPendingId(null);
      setActionError({ id: binding.id, message: bindRes.error || 'Could not update this agent to the new version.' });
      return;
    }
    await unbindAgentIntentBinding(agentId, binding.id);
    setActionPendingId(null);
    setAuthoringNotice('Updated to the latest approved version. The newly authorized tools are now enforced.');
    await load();
  };

  const counts = useMemo(() => {
    const c = { ALL: bindings.length, ACTIVE: 0, STALE: 0, ORPHANED: 0 };
    for (const b of bindings) {
      if (c[b.state] !== undefined) c[b.state]++;
    }
    return c;
  }, [bindings]);

  return (
    <div>
      {/* Phase 2C RuntimeSourceWidget DELETED in V219 (Phase 5). */}

      {/* The Bind step's place in the Intent pipeline. After a template version
          is APPROVED in the catalog, Bind & Materialize is the NEXT STEP here:
          it reuses the policy-skill extract-bind path to compile the version's
          Rego into an agent-scoped PolicyRule the gateway enforces + seals. */}
      <div style={{ marginBottom: 16 }}>
        <IntentLayersExplainer active="bind" />
      </div>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap', marginBottom: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12, color: 'var(--v2-text-secondary)' }}>
          <FilterIcon size={12} />
          <CountChip label="Total" count={counts.ALL} />
          <CountChip label="Active" count={counts.ACTIVE} color="var(--v2-success)" bg="var(--v2-success-soft)" />
          {counts.STALE > 0    && <CountChip label="Stale"    count={counts.STALE}    color="var(--v2-warning)" bg="var(--v2-warning-soft)" />}
          {counts.ORPHANED > 0 && <CountChip label="Orphaned" count={counts.ORPHANED} color="var(--v2-danger)" bg="var(--v2-danger-soft)" />}
          {needsAuthTotal > 0 && (
            <CountChip label="Tools need authorizing" count={needsAuthTotal} color="var(--v2-warning)" bg="var(--v2-warning-soft)" />
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            variant="secondary"
            size="sm"
            iconLeft={<RefreshCw className={state === 'loading' ? 'animate-spin' : ''} />}
            onClick={load}
            disabled={state === 'loading'}
            title="Reload bindings"
          >
            Reload
          </Button>
          <Button
            variant="primary"
            size="sm"
            iconLeft={<Plus />}
            onClick={() => setBindOpen(true)}
            disabled={!canWrite}
            title={canWrite
              ? 'Bind this agent to an APPROVED template version — materializes its Rego into an agent-scoped PolicyRule.'
              : 'You need agent.write, compliance.admin, or policy.admin to bind.'}
          >
            Bind &amp; Materialize
          </Button>
        </div>
      </div>

      {/* Error states */}
      {state === 'error' && <InfoBanner kind="error">{errorMsg}</InfoBanner>}
      {actionError && actionError.id === 'panel' && (
        <InfoBanner kind="error">{actionError.message}</InfoBanner>
      )}
      {authoringNotice && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', marginBottom: 12,
          color: 'var(--v2-success)', background: 'var(--v2-success-soft)',
          border: '1px solid var(--v2-success-soft)', borderRadius: 6, fontSize: 12, lineHeight: 1.4,
        }}>
          <CheckCircle2 size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span style={{ flex: 1 }}>{authoringNotice}</span>
          <button
            type="button"
            onClick={() => setAuthoringNotice(null)}
            style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--v2-success)', fontWeight: 600 }}
          >Dismiss</button>
        </div>
      )}

      {/* Loading */}
      {state === 'loading' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 16, color: 'var(--v2-text-muted)', fontSize: 13 }}>
          <Loader2 size={14} className="animate-spin" /> Loading bindings…
        </div>
      )}

      {/* Empty */}
      {state === 'ready' && bindings.length === 0 && (
        <div style={{
          padding: 24, textAlign: 'center', background: 'var(--v2-bg-muted)',
          border: '1px dashed var(--v2-border-strong)', borderRadius: 8, color: 'var(--v2-text-muted)', fontSize: 13,
        }}>
          <Boxes size={28} color="var(--v2-border-strong)" style={{ marginBottom: 8 }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--v2-text-primary)', marginBottom: 4 }}>
            Next step: Bind &amp; Materialize
          </div>
          <p style={{ margin: '0 auto', maxWidth: 520, lineHeight: 1.5 }}>
            This agent has no intent binding yet. Once a template version is{' '}
            <strong>APPROVED</strong> in the catalog,{' '}
            {canWrite
              ? 'click "Bind & Materialize" to attach it here — that compiles its Rego into an agent-scoped PolicyRule the gateway enforces + seals (INTENT) on every governed call.'
              : 'a Developer or Compliance reviewer binds it here to materialize the runtime PolicyRule that lights the Intent prove.'}
          </p>
        </div>
      )}

      {/* List */}
      {state === 'ready' && bindings.length > 0 && (
        <div style={{
          background: 'var(--v2-bg-surface)', border: '1px solid var(--v2-border-default)', borderRadius: 8,
          overflow: 'hidden',
        }}>
          {bindings.map((b, idx) => (
            <BindingRow
              key={b.id}
              binding={b}
              isLast={idx === bindings.length - 1}
              canWrite={canWrite}
              agentPhase={agentPhase}
              agentId={agentId}
              skillNameById={skillNameById}
              initialCoverage={coverageById[b.id]}
              onAuthorizeNewVersion={onAuthorizeNewVersion}
              onUpdateToVersion={onUpdateToVersion}
              authoringBusy={authoringBusyId === b.id}
              isPending={actionPendingId === b.id}
              inlineError={actionError && actionError.id === b.id ? actionError.message : null}
              onUnbind={() => runAction(b.id, () => unbindAgentIntentBinding(agentId, b.id))}
              onRebind={() => runAction(b.id, () => rebindAgentIntentBinding(agentId, b.id))}
              onDetectDrift={() => runAction(b.id, () => detectAgentIntentBindingDrift(agentId, b.id))}
              onNarrow={(ids) => runAction(b.id, () => narrowAgentIntentBinding(agentId, b.id, ids))}
            />
          ))}
        </div>
      )}

      <BindToTemplateSlideout
        isOpen={bindOpen}
        agentId={agentId}
        onClose={() => setBindOpen(false)}
        onBound={() => { setBindOpen(false); load(); }}
      />

      {/* Inline "new version" authoring — reuses the shared version editor, opened here so the
          operator never leaves the agent to authorize newly-connected tools. */}
      <EditIntentTemplateVersionSlideout
        version={editorVersion}
        templateDisplayName={editorTemplateName}
        onClose={() => setEditorVersion(null)}
        onSaved={() => {
          setEditorVersion(null);
          setAuthoringNotice('New version saved as a draft. Submit and approve it in Intent Profiles — then an “Update to the new version” action appears on this agent’s binding to enforce the added tools.');
          load();
        }}
      />
    </div>
  );
}

/* ── binding row ─────────────────────────────────────────────────── */
function BindingRow({
  binding, isLast, canWrite, agentPhase, agentId, skillNameById, initialCoverage,
  onAuthorizeNewVersion, onUpdateToVersion, authoringBusy, isPending, inlineError,
  onUnbind, onRebind, onDetectDrift, onNarrow,
}) {
  const [narrowOpen, setNarrowOpen] = useState(false);
  const [narrowSelected, setNarrowSelected] = useState([]); // skill ids to newly exclude

  // Authorization coverage — seeded from the parent's eager fetch; re-fetched on demand if absent.
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [coverage, setCoverage] = useState(initialCoverage || null);
  const [coverageState, setCoverageState] = useState(initialCoverage ? 'ready' : 'idle'); // idle | loading | ready | error

  // Keep in sync when the parent's eager coverage arrives or refreshes after an action.
  useEffect(() => {
    if (initialCoverage) { setCoverage(initialCoverage); setCoverageState('ready'); }
  }, [initialCoverage]);

  const resolvedIds = binding.resolvedSkillIds ?? [];
  const excludedIds = binding.narrowingExcludedSkillIds ?? [];
  const resolvedCount = resolvedIds.length;
  const excludedCount = excludedIds.length;
  const excludedSet = new Set(excludedIds.map(String));

  // Legible intent name + version (from the enriched fields; fall back to the version id).
  const intentName = binding.templateDisplayName || binding.templateName
    || (binding.templateVersionId ? 'Intent ' + shortId(binding.templateVersionId) : 'Intent');
  const versionLabel = binding.versionNumber != null ? ('v' + binding.versionNumber) : null;

  const loadCoverage = async () => {
    setCoverageState('loading');
    const res = await getAgentIntentBindingCoverage(agentId, binding.id);
    if (res?.ok === false) { setCoverageState('error'); return; }
    setCoverage(res?.data ?? res);
    setCoverageState('ready');
  };

  const toggleCoverage = () => {
    const next = !coverageOpen;
    setCoverageOpen(next);
    if (next && coverageState === 'idle') loadCoverage();
  };

  // Open a new draft version of this intent inline, pre-seeded to authorize the not-yet-allowed tools.
  const handleAuthorize = () => onAuthorizeNewVersion(binding, coverage);

  const submitNarrow = () => {
    if (narrowSelected.length === 0) return;
    onNarrow(narrowSelected);
    setNarrowOpen(false);
    setNarrowSelected([]);
  };

  return (
    <div style={{
      padding: 14,
      borderBottom: isLast ? 'none' : '1px solid var(--v2-border-default)',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* binding line — the intent's name + version, state, phase */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <Link2 size={13} color="var(--v2-text-muted)" />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--v2-text-primary)' }}>
              {intentName}
            </span>
            {versionLabel && (
              <span style={{
                fontSize: 11, fontWeight: 600, color: 'var(--v2-text-secondary)',
                background: 'var(--v2-bg-muted)', padding: '1px 6px', borderRadius: 999,
              }}>{versionLabel}</span>
            )}
            <BindingStateBadge state={binding.state} />
            <PhaseBadge phase={binding.attachedInPhase} />
          </div>
          <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', marginBottom: 6 }}>
            source version <code style={{ fontSize: 10 }}>{shortId(binding.templateVersionId)}</code>
            {binding.versionState ? ' · ' + String(binding.versionState).toLowerCase() : ''}
          </div>

          {/* stats row */}
          <div style={{ fontSize: 12, color: 'var(--v2-text-secondary)', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <span><Activity size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              <strong>{resolvedCount}</strong> resolved skill{resolvedCount === 1 ? '' : 's'}
            </span>
            {excludedCount > 0 && (
              <span style={{ color: 'var(--v2-warning)' }}>
                <Link2Off size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                <strong>{excludedCount}</strong> narrowed
              </span>
            )}
            <span><GitBranch size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              attached {fmtDate(binding.attachedAt)}
            </span>
            {binding.lastDriftCheckAt && (
              <span><RefreshCw size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                drift checked {fmtDate(binding.lastDriftCheckAt)}
              </span>
            )}
          </div>

          {/* J3/J6 — materialization to the agent runtime. A bound version that
              carries an intent Rego is compiled into an agent-scoped PolicyRule
              (reusing the policy-skill extract-bind pipeline); the gateway then
              auto-evaluates it + seals INTENT on every governed call. */}
          {binding.boundPolicyRuleId ? (
            <div style={{
              marginTop: 8, fontSize: 12, color: 'var(--v2-success)',
              background: 'var(--v2-success-soft)', borderRadius: 6, padding: '6px 10px',
              display: 'flex', gap: 8, alignItems: 'flex-start',
            }}>
              <ShieldCheck size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                Enforcing on every request — each protected tool call is checked against this intent
                before it runs.
              </span>
            </div>
          ) : (
            <div style={{
              marginTop: 8, fontSize: 12, color: 'var(--v2-text-muted)',
              background: 'var(--v2-bg-muted)', borderRadius: 6, padding: '6px 10px',
              display: 'flex', gap: 8, alignItems: 'flex-start',
            }}>
              <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                Not fully enforcing yet. Bind an approved version of this intent to turn on
                per-request checks.
              </span>
            </div>
          )}

          {/* A newer approved version of this intent exists — offer to move the agent to it. */}
          {binding.hasNewerApprovedVersion && canWrite && (
            <div style={{
              marginTop: 8, fontSize: 12, color: 'var(--v2-warning)', background: 'var(--v2-warning-soft)',
              borderRadius: 6, padding: '8px 10px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
            }}>
              <AlertTriangle size={13} style={{ flexShrink: 0 }} />
              <span style={{ flex: 1, minWidth: 160 }}>
                A newer approved version (v{binding.latestApprovedVersionNumber}) of this intent is available.
                This agent is still on {versionLabel || 'the current version'} — update to enforce its rules.
              </span>
              <Button size="sm" variant="primary" loading={isPending} onClick={() => onUpdateToVersion(binding)}>
                Update to v{binding.latestApprovedVersionNumber}
              </Button>
            </div>
          )}

          {/* Authorization coverage — which of this agent's connector tools the intent allows. */}
          <CoveragePanel
            open={coverageOpen}
            onToggle={toggleCoverage}
            loadState={coverageState}
            coverage={coverage}
            canWrite={canWrite}
            authorizing={authoringBusy}
            onAuthorize={handleAuthorize}
          />

          {/* stale / orphaned reason */}
          {binding.staleReason && (
            <div style={{
              marginTop: 8, fontSize: 12, color: 'var(--v2-warning)',
              background: 'var(--v2-warning-soft)', border: '1px solid var(--v2-warning-soft)',
              borderRadius: 6, padding: '6px 10px',
              display: 'flex', gap: 8, alignItems: 'flex-start',
            }}>
              <ShieldAlert size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{binding.staleReason}</span>
            </div>
          )}

          {inlineError && (
            <div style={{
              marginTop: 8, fontSize: 12, color: 'var(--v2-danger)', background: 'var(--v2-danger-soft)',
              border: '1px solid var(--v2-danger-soft)', borderRadius: 6, padding: '8px 10px',
              display: 'flex', gap: 8, alignItems: 'flex-start',
            }}>
              <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{inlineError}</span>
            </div>
          )}
        </div>

        {/* action buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0, alignItems: 'flex-end' }}>
          {isPending && <Loader2 size={14} className="animate-spin" style={{ color: 'var(--v2-text-muted)' }} />}
          {!narrowOpen && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button
                size="sm" variant="secondary"
                iconLeft={<RefreshCw />}
                onClick={onDetectDrift}
                disabled={isPending}
                title="Re-check this binding for drift against current skills + template state."
              >
                Check drift
              </Button>
              {canWrite && (
                <>
                  <Button
                    size="sm" variant="secondary"
                    onClick={onRebind}
                    disabled={isPending}
                    title="Re-resolve matchers against the agent's current skills. Preserves narrowing exclusions."
                  >
                    Rebind
                  </Button>
                  <Button
                    size="sm" variant="secondary"
                    iconLeft={<Link2Off />}
                    onClick={() => setNarrowOpen(true)}
                    disabled={isPending}
                    title="Add skills to the exclusion list. Stricter-only — Act-phase safe."
                  >
                    Narrow…
                  </Button>
                  <Button
                    size="sm" variant="danger"
                    onClick={onUnbind}
                    disabled={isPending}
                    title="Detach this binding + tear down its materialized PolicyRule. Allowed in any phase. Audit row survives via template_version_id RESTRICT."
                  >
                    Unbind
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* inline narrow prompt — pick skills to exclude, by name */}
      {narrowOpen && (
        <div style={{
          marginTop: 12, padding: 12, background: 'var(--v2-bg-muted)',
          border: '1px solid var(--v2-border-strong)', borderRadius: 6,
        }}>
          <label style={{
            display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--v2-text-secondary)',
            marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em',
          }}>Exclude skills from this intent</label>
          <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', marginBottom: 8, lineHeight: 1.4 }}>
            Tick the skills this agent should not be allowed to use under this intent. This only tightens
            access — it never adds skills.{excludedCount > 0 ? ` ${excludedCount} already excluded.` : ''}
          </div>
          {resolvedIds.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--v2-text-muted)' }}>No resolved skills to narrow.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
              {resolvedIds.map((sid) => {
                const idStr = String(sid);
                const already = excludedSet.has(idStr);
                const checked = already || narrowSelected.includes(idStr);
                return (
                  <label key={idStr} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6,
                    background: already ? 'var(--v2-bg-muted)' : 'var(--v2-bg-surface)',
                    border: '1px solid var(--v2-border-default)', opacity: already ? 0.6 : 1,
                    cursor: already ? 'not-allowed' : 'pointer',
                  }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={already}
                      onChange={(e) => setNarrowSelected((prev) => e.target.checked
                        ? [...prev, idStr] : prev.filter((x) => x !== idStr))}
                    />
                    <span style={{ fontSize: 12, color: 'var(--v2-text-primary)' }}>
                      {skillNameById[idStr] || shortId(idStr)}
                    </span>
                    {already && (
                      <span style={{ fontSize: 10, color: 'var(--v2-text-muted)', marginLeft: 'auto' }}>
                        already excluded
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
            <Button size="sm" variant="secondary" onClick={() => { setNarrowOpen(false); setNarrowSelected([]); }}>
              Cancel
            </Button>
            <Button size="sm" variant="primary" onClick={submitNarrow} disabled={narrowSelected.length === 0}>
              Exclude{narrowSelected.length ? ` ${narrowSelected.length}` : ''}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── "Bind to template version" slideout ────────────────────────── */
function BindToTemplateSlideout({ isOpen, agentId, onClose, onBound }) {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // Load templates whenever the slideout opens
  useEffect(() => {
    if (!isOpen) return;
    setErrorMsg(null);
    setSelectedTemplateId(null);
    setSelectedVersionId(null);
    setVersions([]);
    setLoadingTemplates(true);
    (async () => {
      const res = await listIntentTemplates();
      setLoadingTemplates(false);
      if (res?.ok === false) {
        setErrorMsg(res.error || 'Failed to load templates');
        return;
      }
      const raw = res?.data ?? res;
      const arr = Array.isArray(raw) ? raw : [];
      // Only ACTIVE templates are bindable (DRAFT has no APPROVED version;
      // DEPRECATED/ARCHIVED are out of bounds for new binds).
      setTemplates(arr.filter((t) => t.state === 'ACTIVE'));
    })();
  }, [isOpen]);

  // Load versions when template selection changes
  useEffect(() => {
    if (!selectedTemplateId) { setVersions([]); setSelectedVersionId(null); return; }
    setLoadingVersions(true);
    (async () => {
      const res = await listIntentTemplateVersions(selectedTemplateId);
      setLoadingVersions(false);
      if (res?.ok === false) {
        setErrorMsg(res.error || 'Failed to load versions');
        return;
      }
      const raw = res?.data ?? res;
      const arr = Array.isArray(raw) ? raw : [];
      // Backend bind() rejects non-APPROVED. Filter client-side too so the
      // operator only sees bindable options.
      const approved = arr.filter((v) => v.state === 'APPROVED');
      setVersions(approved);
      // Default to the highest-numbered APPROVED version (typically the active one)
      if (approved.length > 0) {
        const sorted = [...approved].sort((a, b) => b.versionNumber - a.versionNumber);
        setSelectedVersionId(sorted[0].id);
      } else {
        setSelectedVersionId(null);
      }
    })();
  }, [selectedTemplateId]);

  const handleBind = async () => {
    if (!selectedVersionId) return;
    setSubmitting(true);
    setErrorMsg(null);
    const res = await bindAgentToIntentTemplateVersion(agentId, selectedVersionId);
    setSubmitting(false);
    if (res?.ok === false) {
      setErrorMsg(res.error || 'Failed to bind');
      return;
    }
    if (onBound) onBound();
  };

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={540}
      ariaLabel="Bind agent to template version"
      header={
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--v2-border-default)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--v2-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <Link2 size={12} />
            <span>Bind to template version</span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--v2-text-primary)', marginTop: 6 }}>
            Attach this agent to an intent template
          </div>
          <div style={{ fontSize: 12, color: 'var(--v2-text-muted)', marginTop: 2 }}>
            Only APPROVED template versions are bindable. The binding freezes the resolved
            skill IDs at attach time; new agent skills do not silently widen — rebind to refresh.
          </div>
        </div>
      }
      footer={
        <div style={{
          padding: '12px 18px', borderTop: '1px solid var(--v2-border-default)', background: 'var(--v2-bg-muted)',
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleBind} disabled={!selectedVersionId || submitting}>
            {submitting ? <><Loader2 size={14} className="animate-spin" /> Binding…</> : 'Bind & Materialize'}
          </Button>
        </div>
      }
    >
      <div style={{ padding: '20px 24px' }}>
        {errorMsg && <InfoBanner kind="error">{errorMsg}</InfoBanner>}

        <FieldLabel>Template</FieldLabel>
        {loadingTemplates ? (
          <Inline><Loader2 size={14} className="animate-spin" /> Loading templates…</Inline>
        ) : templates.length === 0 ? (
          <div style={{
            padding: 12, background: 'var(--v2-bg-muted)', border: '1px dashed var(--v2-border-strong)',
            borderRadius: 6, fontSize: 13, color: 'var(--v2-text-muted)',
          }}>
            No ACTIVE templates yet. Compliance must author + approve a template
            before agents can be bound.
          </div>
        ) : (
          <select
            value={selectedTemplateId || ''}
            onChange={(e) => setSelectedTemplateId(e.target.value || null)}
            style={inputStyle}
          >
            <option value="">— select a template —</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.displayName || t.name}{t.category ? ` (${t.category})` : ''}
              </option>
            ))}
          </select>
        )}

        {selectedTemplateId && (
          <>
            <FieldLabel style={{ marginTop: 20 }}>Version</FieldLabel>
            {loadingVersions ? (
              <Inline><Loader2 size={14} className="animate-spin" /> Loading versions…</Inline>
            ) : versions.length === 0 ? (
              <div style={{
                padding: 12, background: 'var(--v2-bg-muted)', border: '1px dashed var(--v2-border-strong)',
                borderRadius: 6, fontSize: 13, color: 'var(--v2-text-muted)',
              }}>
                No APPROVED versions on this template. The template is ACTIVE but its versions
                may all be DRAFT/IN_REVIEW/DEPRECATED — bind is blocked until at least one
                APPROVED version exists.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {versions.map((v) => (
                  <label
                    key={v.id}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10,
                      padding: 10, border: '1px solid ' + (selectedVersionId === v.id ? 'var(--v2-accent-cyan)' : 'var(--v2-border-default)'),
                      background: selectedVersionId === v.id ? 'var(--v2-accent-cyan-soft)' : 'var(--v2-bg-surface)',
                      borderRadius: 6, cursor: 'pointer',
                    }}
                  >
                    <input
                      type="radio"
                      name="versionId"
                      value={v.id}
                      checked={selectedVersionId === v.id}
                      onChange={() => setSelectedVersionId(v.id)}
                      style={{ marginTop: 3 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <strong style={{ fontSize: 13 }}>v{v.versionNumber}</strong>
                        {v.securityCritical && (
                          <span title={v.securityCriticalReason}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: 3,
                                  fontSize: 10, fontWeight: 600, color: 'var(--v2-danger)', background: 'var(--v2-danger-soft)',
                                  padding: '1px 5px', borderRadius: 999, textTransform: 'uppercase',
                                }}>
                            <ShieldAlert size={9} /> Critical
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', marginTop: 2 }}>
                        Approved {fmtDate(v.approvedAt)}
                        {' · '}
                        <span style={{ fontFamily: 'ui-monospace, monospace' }}>{shortId(v.id)}</span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Slideout>
  );
}

/* ── authorization coverage panel ───────────────────────────────── */
function CoveragePanel({ open, onToggle, loadState, coverage, canWrite, authorizing, onAuthorize }) {
  const authorized = coverage?.authorized ?? [];
  const unauthorized = coverage?.unauthorized ?? [];
  // Tools this intent could not be checked against. Kept apart from the ones it refuses: one is a
  // decision someone made, the other is a fault to resolve, and they need different actions.
  const unevaluated = coverage?.unevaluated ?? [];
  const callableCount = coverage?.callableCount ?? 0;
  const hasGap = unauthorized.length > 0;
  const hasUnchecked = unevaluated.length > 0;
  const plural = (n) => (n === 1 ? '' : 's');

  return (
    <div style={{ marginTop: 8, border: '1px solid var(--v2-border-default)', borderRadius: 6, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px',
          background: 'var(--v2-bg-muted)', border: 'none', cursor: 'pointer', textAlign: 'left',
          fontSize: 12, fontWeight: 600, color: 'var(--v2-text-secondary)',
        }}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <ShieldCheck size={13} />
        <span>Tool access under this intent</span>
        {loadState === 'ready' && (
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <CountChip label="Allowed" count={authorized.length} color="var(--v2-success)" bg="var(--v2-success-soft)" />
            {hasGap && <CountChip label="Not allowed" count={unauthorized.length} color="var(--v2-warning)" bg="var(--v2-warning-soft)" />}
            {hasUnchecked && <CountChip label="Needs attention" count={unevaluated.length} color="var(--v2-danger)" bg="var(--v2-danger-soft)" />}
          </span>
        )}
      </button>

      {open && (
        <div style={{ padding: 10 }}>
          {loadState === 'loading' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--v2-text-muted)' }}>
              <Loader2 size={13} className="animate-spin" /> Checking which tools this intent allows…
            </div>
          )}
          {loadState === 'error' && (
            <div style={{ fontSize: 12, color: 'var(--v2-danger)' }}>Couldn&rsquo;t check tool access. Try reloading.</div>
          )}
          {loadState === 'ready' && callableCount === 0 && (
            <div style={{ fontSize: 12, color: 'var(--v2-text-muted)' }}>
              This agent has no connected data tools yet. Connect a data source, then its tools appear here to authorize.
            </div>
          )}
          {loadState === 'ready' && callableCount > 0 && (
            <>
              {hasUnchecked && (
                <div style={{
                  fontSize: 12, color: 'var(--v2-danger)', background: 'var(--v2-danger-soft)',
                  borderRadius: 6, padding: '8px 10px', marginBottom: 10, lineHeight: 1.5,
                  display: 'flex', gap: 8, alignItems: 'flex-start',
                }}>
                  <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>
                    <strong>{unevaluated.length}</strong> of {callableCount} connected tool{plural(callableCount)} could
                    not be checked against this intent, so {unevaluated.length === 1 ? 'its' : 'their'} access
                    cannot be confirmed either way. Re-save the intent to check again. If it keeps
                    happening, the intent&rsquo;s rules need review — adding {unevaluated.length === 1 ? 'it' : 'them'} to
                    a new version will not resolve this.
                  </span>
                </div>
              )}
              {hasGap && (
                <div style={{
                  fontSize: 12, color: 'var(--v2-warning)', background: 'var(--v2-warning-soft)',
                  borderRadius: 6, padding: '8px 10px', marginBottom: 10, lineHeight: 1.5,
                }}>
                  <strong>{unauthorized.length}</strong> of {callableCount} connected tool{plural(callableCount)} {unauthorized.length === 1 ? 'is' : 'are'} not
                  authorized by this intent. Calls to {unauthorized.length === 1 ? 'it' : 'them'} are blocked until you add {unauthorized.length === 1 ? 'it' : 'them'} to a new version.
                </div>
              )}
              {!hasGap && !hasUnchecked && (
                <div style={{
                  fontSize: 12, color: 'var(--v2-success)', background: 'var(--v2-success-soft)',
                  borderRadius: 6, padding: '8px 10px', marginBottom: 10,
                }}>
                  All {callableCount} connected tool{plural(callableCount)} are authorized by this intent.
                </div>
              )}

              {authorized.length > 0 && (
                <ToolChipRow label="Authorized" tone="success" icon={<CheckCircle2 size={11} />} tools={authorized} />
              )}
              {unauthorized.length > 0 && (
                <ToolChipRow label="Not authorized" tone="warning" icon={<XCircle size={11} />} tools={unauthorized} />
              )}
              {unevaluated.length > 0 && (
                <ToolChipRow label="Could not be checked" tone="danger" icon={<AlertTriangle size={11} />} tools={unevaluated} />
              )}

              {hasGap && canWrite && (
                <div style={{ marginTop: 10 }}>
                  <Button size="sm" variant="primary" iconLeft={<FilePlus2 size={13} />} loading={authorizing} onClick={onAuthorize}>
                    Authorize {unauthorized.length} in a new version
                  </Button>
                  <div style={{ fontSize: 11, color: 'var(--v2-text-muted)', marginTop: 6, lineHeight: 1.4 }}>
                    Opens a new draft of this intent here, pre-filled to allow these tools. Review, then
                    submit &amp; approve it and Rebind to enforce.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ToolChipRow({ label, tone, icon, tools }) {
  const color = tone === 'success' ? 'var(--v2-success)'
    : tone === 'danger' ? 'var(--v2-danger)'
      : 'var(--v2-warning)';
  const bg = tone === 'success' ? 'var(--v2-success-soft)'
    : tone === 'danger' ? 'var(--v2-danger-soft)'
      : 'var(--v2-warning-soft)';
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{
        fontSize: 10, fontWeight: 600, color: 'var(--v2-text-muted)', textTransform: 'uppercase',
        letterSpacing: '0.04em', marginBottom: 4,
      }}>{label} ({tools.length})</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {tools.map((t) => (
          <span key={t} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color, background: bg,
            padding: '2px 7px', borderRadius: 999, fontFamily: 'ui-monospace, SFMono-Regular, monospace',
          }}>
            {icon}{t}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── presentation primitives ────────────────────────────────────── */
function CountChip({ label, count, color = 'var(--v2-text-secondary)', bg = 'var(--v2-bg-muted)' }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 8px', fontSize: 11, fontWeight: 600,
      color, background: bg, borderRadius: 999,
    }}>
      {label}
      <span style={{ fontWeight: 500, opacity: 0.7 }}>{count}</span>
    </span>
  );
}

/* Phase 2C RuntimeSourceWidget component DELETED in V219 (Phase 5). */

function BindingStateBadge({ state }) {
  const styles = {
    ACTIVE:   { color: 'var(--v2-success)', bg: 'var(--v2-success-soft)' },
    STALE:    { color: 'var(--v2-warning)', bg: 'var(--v2-warning-soft)' },
    ORPHANED: { color: 'var(--v2-danger)', bg: 'var(--v2-danger-soft)' },
  };
  const s = styles[state] || styles.STALE;
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, color: s.color, background: s.bg,
      padding: '2px 6px', borderRadius: 999, textTransform: 'uppercase', letterSpacing: '0.05em',
    }}>{state || 'UNKNOWN'}</span>
  );
}

function PhaseBadge({ phase }) {
  if (!phase) return null;
  return (
    <span style={{
      fontSize: 10, fontWeight: 600, color: 'var(--v2-text-secondary)', background: 'var(--v2-bg-muted)',
      padding: '2px 6px', borderRadius: 999, letterSpacing: '0.05em',
    }}>attached in {phase}</span>
  );
}

function InfoBanner({ kind, children }) {
  const styles = {
    error: { color: 'var(--v2-danger)', bg: 'var(--v2-danger-soft)', border: 'var(--v2-danger-soft)' },
    warn:  { color: 'var(--v2-warning)', bg: 'var(--v2-warning-soft)', border: 'var(--v2-warning-soft)' },
  };
  const s = styles[kind] || styles.warn;
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      padding: '10px 12px', marginBottom: 12,
      color: s.color, background: s.bg, border: `1px solid ${s.border}`,
      borderRadius: 6, fontSize: 12, lineHeight: 1.4,
    }}>
      <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
      <span>{children}</span>
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '8px 10px', fontSize: 13,
  border: '1px solid var(--v2-border-strong)', borderRadius: 6, background: 'var(--v2-bg-surface)',
  color: 'var(--v2-text-primary)', outline: 'none', fontFamily: 'inherit',
};

function FieldLabel({ children, style }) {
  return (
    <label style={{
      display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--v2-text-secondary)',
      marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em',
      ...(style || {}),
    }}>{children}</label>
  );
}

function Inline({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--v2-text-muted)' }}>
      {children}
    </div>
  );
}

/* ── helpers ─────────────────────────────────────────────────────── */
// Append a non-destructive allow rule authorizing the given tool tokens (already "mcp:"-prefixed).
// Rego OR-combines allow rules, so this widens the intent without touching the existing rules —
// robust regardless of how the base policy was authored.
function appendAuthorizeBlock(baseRego, tokens) {
  if (!tokens || tokens.length === 0) return baseRego || '';
  const set = '{ ' + tokens.map((t) => JSON.stringify(t)).join(', ') + ' }';
  const block = [
    '',
    '# Authorize additional connected tools (added from Tool access coverage).',
    'prove7_added_tools := ' + set,
    '',
    'allow if {',
    '    prove7_added_tools[input.skill]',
    '    not input.data_class == "restricted"',
    '}',
    '',
  ].join('\n');
  const base = baseRego && baseRego.trim().length > 0
    ? baseRego
    : 'package agentlets.intent\n\ndefault allow := false\n';
  return base.replace(/\s*$/, '\n') + block;
}

function shortId(id) {
  if (!id) return '—';
  return String(id).slice(0, 8);
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}
